import { useEffect, useState, type CSSProperties } from 'react';
import { Button, Checkbox, Drawer, Input, Popconfirm, Spin, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CoreVersion, PromptPipeline } from '@viq-admin/types';
import {
  PIPELINE_LABELS,
  CORE_BODY_MAX,
  listCoreVersions,
  createCoreVersion,
  activateCoreVersion,
} from '../../api/promptStore';
import { MarkdownPromptEditor } from '../../components/MarkdownPromptEditor';
import { Can } from '../../ability/AbilityContext';
import { formatDateTime, space } from '@viq-admin/ui';

/** Shared by the single-version read view and the two-version diff panes. */
const promptPreStyle: CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  border: '1px solid #f0f0f0',
  borderRadius: 6,
  padding: 12,
  maxHeight: 400,
  overflow: 'auto',
};

interface Props {
  pipeline: PromptPipeline;
  open: boolean;
  onClose: () => void;
  /** Parent list's `refreshAll` (`listCorePrompts` + behind-active counts) — called after every mutation so the pipeline's Card (active version / note / versionCount) and the "behind active" banner both stay in sync. */
  onChanged: () => void;
}

/**
 * Version-history drawer for one core pipeline: browse every version in a
 * Table (row-select exactly two → side-by-side raw `<pre>` diff below),
 * create a new version (`MarkdownPromptEditor` + note + "activate
 * immediately"), and per-row activate/rollback. Fetch pattern mirrors
 * `AppliedConferencesModal`/`AppliedCompaniesModal`: bare useEffect +
 * useState + `message.error` — the custom `/v1/superadmin/core-prompts*`
 * endpoints have no corresponding Refine resource, so react-query isn't a
 * fit here.
 *
 * `list.tsx` mounts exactly one of these at a time (conditional on the
 * selected pipeline), so this is also the ONLY place the gated "New version"
 * / "Activate" / "Rollback" controls live — NOT duplicated on the parent
 * Card. Browsing history itself ("Version history", the button that opens
 * this drawer) is a read action and stays ungated on the Card, same
 * rationale as `PromptTemplateDetail`'s "View companies" button; putting a
 * second "New version" control on the Card too would sit in the DOM
 * simultaneously with this drawer's own "New version" toggle once open,
 * which is both redundant and (worse) ambiguous for anything querying by
 * accessible name.
 *
 * Gated on `manage CorePrompt` via `<Can>` (CASL direct) — this screen uses
 * AntD `Drawer`/`Table` rather than the shared capability-registry-routed
 * components, so `CorePrompt` is deliberately NOT added to
 * `consoleCapabilities.ts` (see that file's comment).
 */
export function CoreVersionsDrawer({ pipeline, open, onClose, onChanged }: Props) {
  const [versions, setVersions] = useState<CoreVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<CoreVersion[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [body, setBody] = useState('');
  const [note, setNote] = useState('');
  const [activateNow, setActivateNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  /** Version number the create form was seeded from, for the "starting from" hint. */
  const [seededFrom, setSeededFrom] = useState<number | null>(null);

  function refresh() {
    return listCoreVersions(pipeline).then(setVersions);
  }

  /**
   * Refresh after a SUCCEEDED mutation. The reload is a separate request that
   * can fail on its own (a transient 502, a token blip); awaiting it inside the
   * mutation's try made that land in the mutation's catch, so the operator saw
   * "Version activated" immediately followed by "Failed to activate version"
   * and onChanged() never ran — leaving the parent card and behind-active
   * banner stale while the write had actually succeeded.
   */
  function refreshAfterMutation() {
    return refresh().catch(() =>
      message.warning('Saved, but the version list could not be refreshed — reload to see it.'),
    );
  }

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // Reset any in-progress create form / selection from a previous session —
    // load-bearing if a caller ever switches `pipeline` on an already-open
    // drawer instance rather than closing (unmounting) it first.
    setSelectedRows([]);
    setShowCreate(false);
    setBody('');
    setNote('');
    setActivateNow(false);
    setSeededFrom(null);
    refresh()
      .catch((err) =>
        message.error((err as { message?: string }).message ?? 'Failed to load versions'),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline, open]);

  /**
   * Open the create form seeded with the ACTIVE version's body. Authoring a new
   * core prompt is nearly always a tweak of the live one, and the body is not
   * otherwise readable anywhere in the console — an empty editor invites the
   * operator to retype the prompt from memory and silently drop extraction
   * rules for every company they then roll the result out to.
   */
  function startCreate() {
    const active = versions.find((v) => v.isActive);
    setBody(active?.body ?? '');
    setSeededFrom(active?.version ?? null);
    setShowCreate(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await createCoreVersion(pipeline, { body, note: note || undefined, activate: activateNow });
      message.success('New version created');
      setShowCreate(false);
      setBody('');
      setNote('');
      setActivateNow(false);
      setSeededFrom(null);
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to create version');
      return;
    } finally {
      setSaving(false);
    }
    // Outside the try: the write already succeeded, so a failing reload must not
    // be reported as a failed save.
    await refreshAfterMutation();
    onChanged();
  }

  async function handleActivate(versionId: string) {
    setActivatingId(versionId);
    try {
      await activateCoreVersion(pipeline, versionId);
      message.success('Version activated');
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to activate version');
      return;
    } finally {
      setActivatingId(null);
    }
    await refreshAfterMutation();
    onChanged();
  }

  const activeVersionNumber = versions.find((v) => v.isActive)?.version;

  const columns: ColumnsType<CoreVersion> = [
    {
      dataIndex: 'version',
      key: 'version',
      title: 'Version',
      width: 100,
      render: (v: number) => `Version ${v}`,
    },
    { dataIndex: 'note', key: 'note', title: 'Note', render: (v: string | null) => v ?? '—' },
    {
      dataIndex: 'isActive',
      key: 'isActive',
      title: 'Status',
      width: 100,
      render: (v: boolean) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      title: 'Created',
      width: 170,
      render: (v: string) => formatDateTime(v),
    },
    {
      key: 'actions',
      title: '',
      width: 130,
      render: (_, record) =>
        record.isActive ? null : (
          <Can I="manage" a="CorePrompt" passThrough>
            {(allowed: boolean) => {
              if (!allowed) return null;
              const isRollback = activeVersionNumber != null && record.version < activeVersionNumber;
              return (
                <Popconfirm
                  title={`${isRollback ? 'Roll back to' : 'Activate'} version ${record.version}?`}
                  // The counterintuitive bit, stated at the moment of action:
                  // activation only decides what NEW companies get. Existing
                  // companies keep their pin until an explicit rollout, so it is
                  // easy to believe you have published a change when you have not.
                  description={
                    <span style={{ display: 'block', maxWidth: 300 }}>
                      New companies will use it. Existing companies keep their current version
                      until you roll it out from AI Prompt Rollout.
                    </span>
                  }
                  onConfirm={() => handleActivate(record.id)}
                >
                  <Button size="small" loading={activatingId === record.id}>
                    {isRollback ? 'Rollback' : 'Activate'}
                  </Button>
                </Popconfirm>
              );
            }}
          </Can>
        ),
    },
  ];

  return (
    <Drawer
      title={`${PIPELINE_LABELS[pipeline]} — version history`}
      open={open}
      onClose={onClose}
      width={720}
      extra={
        !showCreate && (
          <Can I="manage" a="CorePrompt" passThrough>
            {(allowed: boolean) =>
              allowed ? (
                <Button type="primary" onClick={startCreate}>
                  New version
                </Button>
              ) : null
            }
          </Can>
        )
      }
    >
      {showCreate && (
        <div style={{ marginBottom: space.xl }} data-testid="core-version-create">
          <Typography.Paragraph type="secondary" style={{ marginBottom: space.sm }}>
            {seededFrom != null
              ? `Starting from version ${seededFrom} (the active prompt) — edit below. Saving always creates a new version; version ${seededFrom} is left unchanged.`
              : 'No active version to start from — this will be the first version for this pipeline.'}
          </Typography.Paragraph>
          <MarkdownPromptEditor
            value={body}
            onChange={setBody}
            max={CORE_BODY_MAX}
            data-testid="core-version-body"
          />
          <Input
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ marginTop: space.sm, marginBottom: space.sm }}
          />
          <Checkbox checked={activateNow} onChange={(e) => setActivateNow(e.target.checked)}>
            Activate immediately
          </Checkbox>
          <div style={{ marginTop: space.sm, display: 'flex', gap: space.sm }}>
            <Button type="primary" loading={saving} disabled={!body} onClick={handleSave}>
              Save version
            </Button>
            <Button
              onClick={() => {
                setShowCreate(false);
                setBody('');
                setNote('');
                setActivateNow(false);
                setSeededFrom(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <Spin />
      ) : (
        <Table<CoreVersion>
          columns={columns}
          dataSource={versions}
          rowKey="id"
          pagination={false}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedRows.map((r) => r.id),
            onChange: (_keys, rows) => setSelectedRows(rows),
          }}
        />
      )}

      {!loading && selectedRows.length === 0 && versions.length > 0 && (
        <Typography.Paragraph type="secondary" style={{ marginTop: space.md }}>
          Select a version to read its prompt; select two to compare them side by side.
        </Typography.Paragraph>
      )}

      {/* Reading a single version — without this the prompt body was visible
          ONLY in the two-row diff, so with one seeded version (day one) the live
          prompt could not be read anywhere in the console. */}
      {selectedRows.length === 1 && (
        <div style={{ marginTop: space.xl }} data-testid="core-version-view">
          <Typography.Text strong>{`Version ${selectedRows[0].version}`}</Typography.Text>
          {selectedRows[0].isActive && (
            <Tag color="green" style={{ marginLeft: space.sm }}>
              Active
            </Tag>
          )}
          <pre style={promptPreStyle}>{selectedRows[0].body}</pre>
        </div>
      )}

      {selectedRows.length === 2 && (
        <div style={{ marginTop: space.xl }}>
          <Typography.Text strong>Diff</Typography.Text>
          <div style={{ display: 'flex', gap: space.md, marginTop: space.sm }}>
            {[...selectedRows]
              .sort((a, b) => a.version - b.version)
              .map((v) => (
                <div key={v.id} style={{ flex: 1, minWidth: 0 }}>
                  <Typography.Text type="secondary">{`Version ${v.version}`}</Typography.Text>
                  <pre style={promptPreStyle}>{v.body}</pre>
                </div>
              ))}
          </div>
        </div>
      )}
    </Drawer>
  );
}
